import { FC } from 'react';
// mui components
import { Grid, Typography, IconButton, Box } from '@mui/material';
import { styled } from '@mui/styles';
// helpers
import { getImageBySize } from 'utils/helpers';
import { getImageUrl, isAudio, isVideo, isVideoOrAudio } from '../helpers';
// types
import { IUploadingFiles } from 'redux-store/api/activity-feed';
import { IStyles } from 'redux-store/types';
// icons
import CancelIcon from 'assets/icons/buttons/cancel-request.svg';
import VideoIcon from 'assets/icons/video-icon.svg';
import AudioIcon from 'assets/icons/audio-icon.svg';
// styles
import { uploadedImagesStyles as styles } from '../styles/styles';

interface IProps {
  item: IUploadingFiles;
  onDelete: (id: string) => void;
  handleOpenModal: (index: number) => void;
  index?: number;
}

const StyledImage = styled('img')(() => styles.mediaItem as IStyles);

const MediaItem: FC<IProps> = ({ item, onDelete, handleOpenModal, index }) => {
  const onOpen = () => {
    if (typeof index === 'number' && !isAudio(item?.mimeType)) {
      handleOpenModal(index);
    }
  };

  const itemUrl = getImageUrl(item);
  const isVideoFile = isVideo(item?.mimeType);
  const isUploadComplete = item.status === 'done';

  const imageUrl = getImageBySize(itemUrl, 64, 64, false, true);

  // Determine what to show based on media type and upload status
  let renderCard;

  if (isAudio(item?.mimeType)) {
    // Audio files - white background with blue icon
    renderCard = <Grid sx={styles.audioContainer}></Grid>;
  } else if (isVideoFile) {
    // Video files
    if (itemUrl) {
      // Has preview URL
      renderCard = <StyledImage src={imageUrl} alt={item.name} />;
    } else if (isUploadComplete) {
      // Upload complete but no preview yet (transcoding in progress) - white background with blue icon
      renderCard = <Grid sx={styles.videoContainer}></Grid>;
    } else {
      // Still uploading
      renderCard = (
        <Grid sx={styles.newImageProgressContainer}>
          <Typography sx={styles.newImageProgressTypo}>{`${item?.percent ?? 100}%`}</Typography>
        </Grid>
      );
    }
  } else {
    // Regular images
    if (itemUrl) {
      renderCard = <StyledImage src={imageUrl} alt={item.name} />;
    } else {
      renderCard = (
        <Grid sx={styles.newImageProgressContainer}>
          <Typography sx={styles.newImageProgressTypo}>{`${item?.percent ?? 100}%`}</Typography>
        </Grid>
      );
    }
  }

  // Only show video icon when we don't have a thumbnail preview yet
  const shouldShowVideoIcon = isVideoFile && !itemUrl;

  const renderVideoAudioIcon = isAudio(item?.mimeType) || shouldShowVideoIcon ? (
    <Grid sx={styles.videoAudioIconContainer}>
      <Box
        sx={{
          '& svg': {
            filter: 'brightness(0) saturate(100%) invert(38%) sepia(83%) saturate(3738%) hue-rotate(214deg) brightness(98%) contrast(93%)',
            width: isVideo(item?.mimeType) ? '28px' : 'auto',
            height: isVideo(item?.mimeType) ? '28px' : 'auto',
          },
        }}
      >
        {isVideo(item?.mimeType) ? <VideoIcon /> : <AudioIcon />}
      </Box>
    </Grid>
  ) : null;

  const renderDuration =
    isVideoOrAudio(item?.mimeType) && item?.duration ? (
      <Typography sx={styles.duration}>{item.duration}</Typography>
    ) : null;

  const renderDeleteButton = (
    <IconButton
      sx={styles.newImageDeleteButton}
      onClick={(event) => {
        event?.stopPropagation();
        onDelete(item.id);
      }}
    >
      <CancelIcon />
    </IconButton>
  );

  return (
    <Grid key={item.tempId} sx={styles.newImagesContainer} onClick={onOpen}>
      {renderCard}
      {renderVideoAudioIcon}
      {renderDuration}
      {renderDeleteButton}
    </Grid>
  );
};

export default MediaItem;
